/**
 * `ddws.rental.v1` messages, hand-encoded for the tenant surface.
 *
 * Same approach as `deployment.ts` and `messages.ts`: the console encodes the
 * protobuf itself rather than pulling a generated client, because the wallet
 * signs bytes and those bytes have to be exactly what the chain will decode.
 *
 * **The field numbers below are the contract, not an implementation detail.**
 * A wrong number produces a signature over a well-formed message the chain
 * decodes differently — so the transaction is rejected as `unauthorized`, or
 * worse, decodes into a valid-but-different quote. Neither failure points at
 * this file.
 *
 * They are therefore pinned against the api repo's own committed golden bytes
 * (`api/rental/testdata/msg_accept_direct_quote.hex`), which is the
 * cross-language check the rental ADR (D5) promises. `rental.test.ts` replays
 * that exact fixture: if api renumbers a field, this file fails rather than
 * signing something the chain will not accept.
 */

import type { GeneratedType } from '@cosmjs/proto-signing'
import { BinaryWriter } from 'cosmjs-types/binary'

/** Mirrors `ddws.rental.v1.RentalMode`. Numbers are the wire values (ADR D3). */
export const RentalModeValue = {
  UNSPECIFIED: 0,
  DIRECT_CUSTOM: 1,
  DIRECT_HOST_EXCLUSIVE: 2,
  DIRECT_HOST_AVAILABLE: 3,
  COMPETITIVE: 4,
} as const

/** The enum NAME → wire number, matching api's JSON marshalling. */
export const rentalModeNumber: Record<string, number> = {
  RENTAL_MODE_UNSPECIFIED: RentalModeValue.UNSPECIFIED,
  RENTAL_MODE_DIRECT_CUSTOM: RentalModeValue.DIRECT_CUSTOM,
  RENTAL_MODE_DIRECT_HOST_EXCLUSIVE: RentalModeValue.DIRECT_HOST_EXCLUSIVE,
  RENTAL_MODE_DIRECT_HOST_AVAILABLE: RentalModeValue.DIRECT_HOST_AVAILABLE,
  RENTAL_MODE_COMPETITIVE: RentalModeValue.COMPETITIVE,
}

export interface DirectQuote {
  chainId: string
  provider: string
  tenant: string
  dseq: bigint
  gseq: number
  oseq: number
  groupSpecHash: Uint8Array
  rentalMode: number
  hostOfferId: string
  price: { denom: string; amount: string }
  expiryHeight: bigint
  nonce: Uint8Array
  signature: Uint8Array

  /**
   * What "entire host" means numerically, signed by the provider (ADR §7 Q2).
   *
   * Required, not optional, and that is deliberate. api's generated marshaller
   * emits field 14 unconditionally because the proto field is
   * `nullable = false`, so a quote object without this property would encode to
   * different bytes than the chain expects. Making it optional here would let a
   * caller omit it and produce a signature the chain rejects.
   */
  hostCapacity: HostCapacity
}

/** ADR §7 Q2. All five are uint64; a JS number cannot hold them safely. */
export interface HostCapacity {
  cpuMillicores: bigint
  memoryBytes: bigint
  storageBytes: bigint
  persistentStorageBytes: bigint
  gpuUnits: bigint
}

export interface MsgAcceptDirectQuote {
  tenant: string
  quote: DirectQuote
}

/** ADR §3: chain_id=1 … signature=13, host_capacity=14. Fields 15–16 reserved. */
function encodeDirectQuote(q: DirectQuote): Uint8Array {
  const w = BinaryWriter.create()

  if (q.chainId !== '') w.uint32(10).string(q.chainId)
  if (q.provider !== '') w.uint32(18).string(q.provider)
  if (q.tenant !== '') w.uint32(26).string(q.tenant)
  if (q.dseq !== 0n) w.uint32(32).uint64(q.dseq)
  if (q.gseq !== 0) w.uint32(40).uint32(q.gseq)
  if (q.oseq !== 0) w.uint32(48).uint32(q.oseq)
  if (q.groupSpecHash.length !== 0) w.uint32(58).bytes(q.groupSpecHash)
  if (q.rentalMode !== 0) w.uint32(64).int32(q.rentalMode)
  if (q.hostOfferId !== '') w.uint32(74).string(q.hostOfferId)

  // price = 10, a non-nullable Coin: emitted whenever either half is set.
  // gogoproto's nullable=false marshals an all-zero Coin as an empty submessage
  // rather than omitting the field, but a quote with no price is invalid
  // anyway — ValidateBasic rejects an unset amount — so the branch only has to
  // agree with api for quotes that could exist.
  if (q.price.denom !== '' || q.price.amount !== '') {
    const coin = BinaryWriter.create()
    if (q.price.denom !== '') coin.uint32(10).string(q.price.denom)
    if (q.price.amount !== '') coin.uint32(18).string(q.price.amount)
    w.uint32(82).bytes(coin.finish())
  }

  if (q.expiryHeight !== 0n) w.uint32(88).uint64(q.expiryHeight)
  if (q.nonce.length !== 0) w.uint32(98).bytes(q.nonce)
  if (q.signature.length !== 0) w.uint32(106).bytes(q.signature)

  // host_capacity = 14, a non-nullable message: emitted ALWAYS, including when
  // every field is zero (which is the DIRECT_CUSTOM case). api's generated
  // marshaller writes tag 0x72 unconditionally because the proto field is
  // `nullable = false`, so a conditional here would drop two bytes and produce
  // a signature over a shorter message than the chain hashes. The inner
  // scalars, by contrast, are omitted when zero — normal proto3 scalar
  // behaviour, and asserted by replaying api's direct-custom vector.
  const cap = BinaryWriter.create()
  if (q.hostCapacity.cpuMillicores !== 0n) cap.uint32(8).uint64(q.hostCapacity.cpuMillicores)
  if (q.hostCapacity.memoryBytes !== 0n) cap.uint32(16).uint64(q.hostCapacity.memoryBytes)
  if (q.hostCapacity.storageBytes !== 0n) cap.uint32(24).uint64(q.hostCapacity.storageBytes)
  if (q.hostCapacity.persistentStorageBytes !== 0n) cap.uint32(32).uint64(q.hostCapacity.persistentStorageBytes)
  if (q.hostCapacity.gpuUnits !== 0n) cap.uint32(40).uint64(q.hostCapacity.gpuUnits)
  w.uint32(114).bytes(cap.finish())

  return w.finish()
}

export const MsgAcceptDirectQuoteMsg = {
  typeUrl: '/ddws.rental.v1.MsgAcceptDirectQuote',

  encode(m: MsgAcceptDirectQuote, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    if (m.tenant !== '') w.uint32(10).string(m.tenant)
    // quote = 2, non-nullable: always emitted. A MsgAcceptDirectQuote without
    // a quote is not a weaker message, it is one the chain rejects — so an
    // omission here would only hide the mistake until signing.
    w.uint32(18).bytes(encodeDirectQuote(m.quote))
    return w
  },

  fromPartial(m: MsgAcceptDirectQuote): MsgAcceptDirectQuote {
    return m
  },

  /**
   * Decoding is not implemented.
   *
   * The console signs and broadcasts; it never reads a `MsgAcceptDirectQuote`
   * back off the chain — the rental queries answer those questions, and a lease
   * reports its own mode. A stub that returned a zero value would be worse
   * than absent: CosmJS calls `decode` when re-parsing a tx it built, and a
   * silently-empty result would look like a message that carried no quote.
   */
  decode(): never {
    throw new Error('ddws.rental.v1.MsgAcceptDirectQuote: decoding is not implemented in the console')
  },
}

export const rentalRegistryTypes: ReadonlyArray<[string, GeneratedType]> = [
  [MsgAcceptDirectQuoteMsg.typeUrl, MsgAcceptDirectQuoteMsg as unknown as GeneratedType],
]
